import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import toast from "react-hot-toast";
import {
  DndContext,
  DragOverlay,
  MeasuringStrategy,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  type SortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import ConfirmModal from "../../../components/modal/ConfirmModal";
import StatusBadge from "../../../components/status-badge/StatusBadge";
import ToolTip from "../../../components/tooltip/ToolTip";
import DropdownMenu, {
  type DropdownMenuItem,
} from "../../../components/dropdown-menu/DropdownMenu";
import FileBrowserShareModal, {
  type FileBrowserShareAdapter,
} from "../../../components/file-browser/FileBrowserShareModal";
import {
  useUpdateNoteMutation,
  useCreateNoteChangeMutation,
  useCreateNoteFolderMutation,
  useCreateNoteMutation,
  useDeleteNoteFolderMutation,
  useDeleteNoteFolderFilesMutation,
  useLazyGetNoteQuery,
  useLazyGetNoteShareLinkQuery,
  useSaveNoteShareLinkMutation,
  useDeleteNoteShareLinkMutation,
  useReorderNotesMutation,
  useUpdateNoteChangeMutation,
  useUpdateNoteFolderMutation,
  type NoteDraftSummary,
  type NoteFolderSummary,
  type NoteSection,
  type NoteSummary,
  type ReorderItem,
} from "../../../app/services/notes";
import { useInitiateUploadMutation } from "../../../app/services/uploadApi";
import {
  containsNativeFiles,
  extractDropEntries,
} from "../../../utils/dataTransferEntries";
import type { FileWithPath } from "../../../hooks/useS3Upload";
import useCopyToClipboard from "../../../hooks/useCopyToClipboard";
import useCollapsedSections from "../../../hooks/useCollapsedSections";
import {
  DROP_DWELL_FILL,
  DROP_DWELL_LINE,
  DROP_DWELL_MS,
  DROP_READY_FILL,
  dropTargetRowStyle,
  useDropDwellGate,
  useDropDwellReady,
} from "../../../hooks/useDropDwell";
import { buildNoteLink, toMarkdownLink } from "../../../utils/deepLink";
import { importNoteEntries } from "../utils/importNotes";
import { downloadNoteAsMarkdown } from "../utils/export";
import {
  draftInsertIndex,
  draftSortOrderAt,
  pickTreeDropTarget,
  type TreeRow,
} from "../utils/treeDnd";
import NoteMoveModal from "./NoteMoveModal";
import NoteRenameModal from "./NoteRenameModal";

/**
 * ノートページ左側のセクション > フォルダ（入れ子可）> ノートのツリー（目次）。
 * カード（共有TIPS）はノートに統合済みで、display_style=card のノートは
 * 行にカードアイコン + 未投稿バッジを添えて区別する。
 *
 * - セクション見出し行はタスクボード/ガントの見出しと同じ配色。先頭の矢印で
 *   セクション全体を開閉できる（開閉状態は localStorage）
 * - フォルダは共有ライブラリと同じ階層構造（parent_id）で、クリックで開閉できる
 * - 作成操作（ノート / フォルダ）は行を増やさないよう、セクション見出し行と
 *   フォルダ行の3点リーダーメニューへ集約する（作成は常にノートから。
 *   カードにしたい場合はエディタ / 閲覧メニューで表示形式を切り替える）
 * - 行の操作は共有ライブラリ（FileBrowser）と同じ体系で、ページ行・フォルダ行
 *   とも右端の3点リーダーにまとめる（「移動...」で移動先を選んで移動できる）
 * - メンテナー以上（can_edit）のセクションでは、行（ノート / フォルダ）を
 *   ドラッグ&ドロップで並べ替えできる。並び順はフォルダ・ノート共通で、
 *   同一コンテナ（セクション直下 / フォルダ直下）内なら種別を問わず自由に
 *   混在できる。フォルダ行の上下端へ落とすと「並び替え」、それ以外の部分に
 *   しばらく留めて行が青くなってから離すと「中に入れる」（ライブラリと同じ
 *   滞留方式。自分・子孫の中へは不可）。ドラッグ中だけツリー末尾に出る
 *   バーへ落とすとセクション直下の末尾へ移動する
 * - コントリビューター以上（can_contribute）には「プルリクエスト (n)」行を表示
 * - セクション設定は can_manage（master_admin）またはセクションオーナー
 *   （can_manage_roles）に表示
 * - OS からの .md / .txt（+ 参照画像）ドロップでノートとして取り込める
 *   （コントリビューター以上）。直接更新できるセクションでは直接作成し、
 *   フォルダ行に落とすとそのフォルダへ、フォルダごと落とすと階層を再現する。
 *   直接更新できないセクションでは下書き（変更提案）として取り込み、
 *   ツリー末尾の「下書き」行から編集・提出する（フォルダ指定は持てない
 *   ためフラット）
 */

const SECTION_HEADER_HEIGHT = 36;

/** コンテナ ID（セクション直下） */
const ROOT = "root";

/** 階層 1 段あたりのインデント幅（px） */
const INDENT_STEP = 16;

const SidebarWrapper = styled.nav`
  width: 100%;
  height: 100%;
  overflow-y: auto;

  @media (max-width: 767px) {
    border-bottom: 1px solid var(--soremo-border);
  }
`;

/* .u-col は align-items: flex-start で子が内容幅に縮むため、
   行を常にサイドバー全幅へストレッチさせる専用コンテナを使う */
const SectionsList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  width: 100%;
`;

const SectionBlock = styled.div<{ $fileDropActive?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;

  /* OS からのファイルドラッグ中はセクション全体を受け皿として示す */
  ${({ $fileDropActive }) =>
    $fileDropActive
      ? `
    outline: 2px dashed var(--soremo-blue);
    outline-offset: -2px;
    background: rgba(0, 154, 206, 0.04);
  `
      : ""}
`;

/**
 * セクション見出し行。ガントの GroupHeaderRow と同じ配色ルール:
 * バナーカラー指定時は `banner-color${N}` クラス（文字色はクラスの
 * `div` セレクタに委ねるため、内容は必ず inner div でラップする）、
 * 未指定時は `--soremo-border` の単色。
 */
const SectionHeaderRow = styled.div.attrs<{ $bannerColor?: string }>(
  ({ $bannerColor }) => ({
    className: $bannerColor ? `banner-color${$bannerColor}` : undefined,
  }),
)<{ $bannerColor?: string }>`
  width: 100%;
  height: ${SECTION_HEADER_HEIGHT}px;
  border-bottom: 1px solid var(--soremo-border);
  ${({ $bannerColor }) =>
    $bannerColor ? "" : "background: var(--soremo-border);"}

  /* banner-color クラスの \`div { color: ... }\` を受けて子へ継承させる */
  .section-header-inner {
    display: flex;
    align-items: center;
    gap: 4px;
    height: 100%;
    padding: 0 8px;

    h3,
    span,
    button,
    .material-symbols-rounded {
      color: inherit;
    }
  }

  .section-title {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

/**
 * セクション見出し行の先頭の開閉矢印。フォルダ行の開閉矢印と同じ
 * 行頭スロット 16px で、開いているときは 90° 回転して下向きにする。
 */
const SectionToggle = styled.button<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 16px;
  height: 20px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;

  .material-symbols-rounded {
    font-size: 20px;
    transform: rotate(${({ $open }) => ($open ? "90deg" : "0deg")});
    transition: transform 0.15s ease;
  }
`;

/**
 * セクション見出しを出さない埋め込み表示用の作成メニュー行。
 * 見出し行が無いと3点リーダーの置き場が無くなるため、右寄せの
 * 細い行として置く（作成権限がある人にだけ表示）。
 */
const SectionActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 2px 8px;
  border-bottom: 1px solid var(--soremo-border);
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 4px;
  flex-shrink: 0;

  .material-symbols-rounded {
    font-size: 18px;
    color: var(--soremo-light-gray);
    transition: color 0.15s;
  }

  @media (hover: hover) {
    &:hover .material-symbols-rounded {
      color: var(--soremo-blue);
    }
  }
`;

/**
 * ノート/カード行。フォルダ行より低く区切り線も持たない（一覧性を優先して
 * 表示件数を増やす）。フォルダ内は階層に応じて段下げする。
 * 右端に3点リーダー（button）を置くため、行自体は button ではなく
 * role="button" の div にする（button の入れ子を避ける）。
 */
const NoteItem = styled.div<{ $selected: boolean; $depth: number }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 28px;
  /* テキスト開始 28px（+ 階層 16px 刻み）= フォルダ行・プルリクエスト行の
     「行頭スロット 16px + gap 4px」と頭を揃える */
  padding: 0 8px 0 ${({ $depth }) => 28 + $depth * INDENT_STEP}px;
  background: ${({ $selected }) =>
    $selected ? "var(--soremo-border)" : "white"};
  cursor: pointer;
  text-align: left;
  transition: background-color 0.2s ease;

  /* ↑↓キーでページを送ると行にフォーカスが移るが、選択中の行は背景色で
     分かるためフォーカス表示は出さない（soremo_style.scss の共通
     フォーカスリング = outline + 外側の青い影 を打ち消す） */
  &&:focus,
  &&:focus-visible {
    outline: none;
    box-shadow: none;
  }

  .note-title {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--soremo-gray);
  }

  /* カード（共有TIPS）行の識別アイコン */
  .card-icon {
    font-size: 16px;
    flex-shrink: 0;
    color: var(--soremo-light-gray);
  }

  /* 3点リーダー（DropdownMenu）。フォルダ行と同じくタッチ端末でも操作
     できるよう常時表示する */
  .note-actions {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  @media (hover: hover) {
    &:hover {
      background: ${({ $selected }) =>
        $selected ? "var(--soremo-border)" : "var(--soremo-background)"};
    }
  }
`;

/**
 * フォルダ行。クリックで開閉、右端の3点リーダーから各操作を行う。
 * 高さはページ行と同じ（一覧性を優先）。区切り線でページ行と見分ける。
 */
const FolderRow = styled.div<{
  $dropActive: boolean;
  $open: boolean;
  $depth: number;
}>`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  height: 28px;
  padding: 0 8px 0 ${({ $depth }) => 8 + $depth * INDENT_STEP}px;
  border-bottom: 1px solid var(--soremo-border);
  background: ${({ $dropActive }) =>
    $dropActive ? "rgba(0, 154, 206, 0.08)" : "white"};
  cursor: pointer;
  transition: background-color 0.15s ease;

  /* 開閉を示す矢印。開いたら 90° 回転して下向きにする。
     幅はライブラリの行頭スロットと同じ 16px に絞り、
     行頭スロット(16px) + gap(4px) でテキスト開始位置を 28px に統一する */
  .folder-arrow {
    font-size: 20px;
    flex-shrink: 0;
    width: 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--soremo-light-gray);
    transform: rotate(${({ $open }) => ($open ? "90deg" : "0deg")});
    transition: transform 0.15s ease;
  }

  .folder-title {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--soremo-gray);
  }

  /* 3点リーダー（DropdownMenu）。タッチ端末でも操作できるよう常時表示 */
  .folder-actions {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  @media (hover: hover) {
    &:hover {
      background: ${({ $dropActive }) =>
        $dropActive ? "rgba(0, 154, 206, 0.08)" : "var(--soremo-background)"};
    }
  }
`;

const FolderRenameInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 2px 6px;
  border: 1px solid var(--soremo-blue);
  border-radius: 4px;
  font-size: 13px;
  outline: none;
`;

const AddSectionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 8px;
  border: none;
  background: transparent;
  color: var(--soremo-light-gray);
  cursor: pointer;
  text-align: left;

  .material-symbols-rounded {
    font-size: 16px;
  }

  @media (hover: hover) {
    &:hover {
      color: var(--soremo-blue);
      background: var(--soremo-background);
    }
  }
`;

/** プルリクエスト（変更提案）一覧への導線行 */
const ChangesRow = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  height: 40px;
  padding: 0 8px;
  border: none;
  border-bottom: 1px solid var(--soremo-border);
  background: ${({ $selected }) =>
    $selected ? "var(--soremo-border)" : "white"};
  cursor: pointer;
  text-align: left;
  color: var(--soremo-light-gray);
  transition: background-color 0.2s ease;

  /* 行頭スロット 16px + gap 4px = テキスト開始 28px。フォルダ行の開閉矢印・
     ライブラリのブックマーク行と同じリズムで頭を揃える */
  .material-symbols-rounded {
    font-size: 16px;
    flex-shrink: 0;
    width: 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .changes-label {
    flex: 1;
    min-width: 0;
  }

  .changes-count {
    flex-shrink: 0;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    background: var(--soremo-blue);
    color: white;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  @media (hover: hover) {
    &:hover {
      background: ${({ $selected }) =>
        $selected ? "var(--soremo-border)" : "var(--soremo-background)"};
      color: var(--soremo-blue);
    }
  }
`;

/**
 * ドラッグ中だけツリー末尾に出す「セクション直下へ移動」のドロップ受け。
 * 見た目は共有ライブラリの RootDropBar と揃える（グレーの破線 = まだ離しても
 * 移動しない / 青の実線 = ここで離せば移動する）。
 */
const RootDropBar = styled.div<{ $active: boolean; $ready: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 8px;
  padding: 8px 12px;
  border: 2px ${({ $ready }) => ($ready ? "solid" : "dashed")}
    ${({ $ready }) => ($ready ? "var(--soremo-blue)" : DROP_DWELL_LINE)};
  border-radius: 6px;
  background-color: white;
  background-image: ${({ $ready }) => {
    const fill = $ready ? DROP_READY_FILL : DROP_DWELL_FILL;
    return `linear-gradient(${fill}, ${fill})`;
  }};
  background-repeat: no-repeat;
  background-size: ${({ $active }) => ($active ? "100% 100%" : "0 100%")};
  color: ${({ $ready }) =>
    $ready ? "var(--soremo-blue)" : "var(--soremo-gray)"};
  transition:
    background-size
      ${({ $active, $ready }) =>
        $active && !$ready ? `${DROP_DWELL_MS}ms linear` : "120ms ease-out"},
    border-color 0.15s ease,
    color 0.15s ease;

  .material-symbols-rounded {
    font-size: 18px;
  }
`;

/**
 * DragOverlay に出す、ドラッグ中の行のゴースト。ポインタの右下へずらし、
 * ドロップ先の行（滞留の進み具合・挿入位置）を隠さないようにする。
 */
const DragGhostRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  max-width: 100%;
  transform: translate(24px, 22px);
  padding: 6px 12px;
  background: white;
  border: 1px solid var(--soremo-border);
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-size: 13px;
  color: var(--soremo-dark-gray, #333);
  pointer-events: none;

  .material-symbols-rounded {
    font-size: 18px;
    color: var(--soremo-light-gray);
  }
`;

/**
 * 並べ替えのプレビューで行をずらさない sortable 戦略。
 * ずらすと見た目の行位置とドロップ判定の位置が食い違い、フォルダの上に
 * 置いたつもりが上下端の「並べ替え」判定になりやすい。代わりに挿入位置へ
 * 線を出す（DropLine）。
 */
const noShiftStrategy: SortingStrategy = () => null;

/** 並べ替えで入る位置を示す線（行の上端 or 下端） */
type DropLine = { id: string; edge: "top" | "bottom" } | null;

const dropLineShadow = (edge: "top" | "bottom"): string =>
  edge === "top"
    ? "inset 0 2px 0 var(--soremo-blue)"
    : "inset 0 -2px 0 var(--soremo-blue)";

// ── D&D ID ヘルパー ──────────────────────────────────────────────────
// draggable: `note:<id>` / `draft:<id>`（自分の下書き）/ `folderdrag:<id>`（フォルダ行）
// droppable: `folder:<id>`（フォルダ行 = フォルダの中へ移動）/
//            `rootzone`（ドラッグ中だけ出るバー = セクション直下の末尾へ移動）
// note: / draft: / folderdrag: は同一コンテナの SortableContext で混在して並び替える

type DndKind = "note" | "draft" | "folder" | "folderdrag" | "rootzone";

const parseDndId = (raw: string): { kind: DndKind; id: string } => {
  if (raw === "rootzone") return { kind: "rootzone", id: "" };
  const sep = raw.indexOf(":");
  return { kind: raw.slice(0, sep) as DndKind, id: raw.slice(sep + 1) };
};

// ── ツリーレイアウト ─────────────────────────────────────────────────

/** parent_id からツリー化したフォルダノード（件数集計・循環チェック用） */
interface FolderNode {
  folder: NoteFolderSummary;
  children: FolderNode[];
}

const flattenNodes = (nodes: FolderNode[]): FolderNode[] =>
  nodes.flatMap((node) => [node, ...flattenNodes(node.children)]);

/**
 * コンテナ（セクション直下 / フォルダ直下）内の 1 行。
 * 下書き（自分の新規作成の変更提案）も配置（folder_id / sort_order）に
 * 従ってツリーの中に並ぶ。
 */
type TreeItem =
  | { kind: "folder"; folder: NoteFolderSummary }
  | { kind: "note"; note: NoteSummary }
  | { kind: "draft"; draft: NoteDraftSummary };

const itemId = (item: TreeItem): string => {
  if (item.kind === "folder") return item.folder.id;
  if (item.kind === "note") return item.note.id;
  return item.draft.id;
};

/** sortable（draggable）用の DnD ID */
const itemDndId = (item: TreeItem): string => {
  if (item.kind === "folder") return `folderdrag:${item.folder.id}`;
  if (item.kind === "note") return `note:${item.note.id}`;
  return `draft:${item.draft.id}`;
};

/** 公開済みの行（フォルダ / ノート）の sort_order */
const itemSortOrder = (
  item: Exclude<TreeItem, { kind: "draft" }>,
): number =>
  item.kind === "folder" ? item.folder.sort_order : item.note.sort_order;

/**
 * セクション内の行（フォルダ + ノート/カード + 自分の下書き）をコンテナごとに
 * 束ねた表示状態。sort_order はフォルダ・ノート共通の並び順空間で、同一
 * コンテナ内なら種別を問わず自由に混在できる（D&D 確定時にコンテナごとの
 * 連番で保存する。下書きは並べ替えの保存対象に含めない）。
 */
interface TreeLayout {
  items: Record<string, TreeItem[]>;
}

const buildLayout = (section: NoteSection): TreeLayout => {
  const folderIds = new Set(section.folders.map((f) => f.id));
  const items: TreeLayout["items"] = { [ROOT]: [] };
  section.folders.forEach((f) => {
    items[f.id] = [];
  });
  // フォルダ → ノートの順に積んでから sort_order で安定ソートする
  // （sort_order が同値のときはフォルダが先 = 混在化以前の表示順と互換）。
  // 親が見えない（不整合等）の行はセクション直下として扱う
  section.folders.forEach((f) => {
    const parent =
      f.parent_id && folderIds.has(f.parent_id) ? f.parent_id : ROOT;
    items[parent].push({ kind: "folder", folder: f });
  });
  section.notes.forEach((n) => {
    const container =
      n.folder_id && folderIds.has(n.folder_id) ? n.folder_id : ROOT;
    items[container].push({ kind: "note", note: n });
  });
  for (const key of Object.keys(items)) {
    items[key].sort(
      (a, b) =>
        itemSortOrder(a as Exclude<TreeItem, { kind: "draft" }>) -
        itemSortOrder(b as Exclude<TreeItem, { kind: "draft" }>),
    );
  }
  // 下書きは sort_order 以上の最初の公開済みの行の手前（null は末尾）に
  // 差し込む。作成順（BE の返却順）に入れるので、同じ位置の下書き同士は
  // 作成順に並ぶ
  section.my_drafts.forEach((draft) => {
    const container =
      draft.folder_id && folderIds.has(draft.folder_id)
        ? draft.folder_id
        : ROOT;
    const list = items[container];
    const published = list.filter(
      (it): it is Exclude<TreeItem, { kind: "draft" }> => it.kind !== "draft",
    );
    const anchor =
      published[
        draftInsertIndex(published.map(itemSortOrder), draft.sort_order)
      ];
    const row: TreeItem = { kind: "draft", draft };
    if (anchor) list.splice(list.indexOf(anchor), 0, row);
    else list.push(row);
  });
  return { items };
};

/** layout 内で行（ノート / フォルダ）が属するコンテナ ID を返す */
const findContainer = (layout: TreeLayout, id: string): string | null => {
  for (const [container, list] of Object.entries(layout.items)) {
    if (list.some((it) => itemId(it) === id)) return container;
  }
  return null;
};

/** 行が属するフォルダ ID（セクション直下なら null） */
const containerFolderId = (layout: TreeLayout, id: string): string | null => {
  const container = findContainer(layout, id);
  return !container || container === ROOT ? null : container;
};

/**
 * layout 上でノートを別コンテナへ移動したレイアウトを返す（純粋関数）。
 * insertBeforeId を指定するとその行の手前へ、なければ末尾へ挿入する。
 */
const moveAcrossContainers = (
  prev: TreeLayout,
  noteId: string,
  to: string,
  insertBeforeId?: string,
): TreeLayout => {
  const from = findContainer(prev, noteId);
  if (!from || from === to) return prev;
  const moving = prev.items[from].find((it) => itemId(it) === noteId);
  if (!moving) return prev;

  const nextFrom = prev.items[from].filter((it) => itemId(it) !== noteId);
  const target = prev.items[to] ?? [];
  const overIndex = insertBeforeId
    ? target.findIndex((it) => itemId(it) === insertBeforeId)
    : -1;
  const insertAt = overIndex < 0 ? target.length : overIndex;
  const nextTo = [
    ...target.slice(0, insertAt),
    moving,
    ...target.slice(insertAt),
  ];

  return { items: { ...prev.items, [from]: nextFrom, [to]: nextTo } };
};

/**
 * 行をコンテナの末尾へ動かしたレイアウトを返す（純粋関数）。
 * ドラッグ中のプレビューで既に同じコンテナへ入っていても末尾へ付け直す
 * （「中に入れる」・セクション直下バー・「移動...」はいずれも末尾に置く）。
 */
const moveToContainerEnd = (
  prev: TreeLayout,
  id: string,
  to: string,
): TreeLayout => {
  const from = findContainer(prev, id);
  if (!from) return prev;
  const moving = prev.items[from].find((it) => itemId(it) === id);
  if (!moving) return prev;
  const items = {
    ...prev.items,
    [from]: prev.items[from].filter((it) => itemId(it) !== id),
  };
  items[to] = [...(items[to] ?? []), moving];
  return { items };
};

/**
 * コンテナ内で activeId の行を overId の行の位置へ動かしたレイアウトを返す
 * （どちらかの行が見つからない・位置が変わらないときは null = 保存不要）。
 */
const reorderWithin = (
  prev: TreeLayout,
  container: string,
  activeId: string,
  overId: string,
): TreeLayout | null => {
  const list = prev.items[container] ?? [];
  const oldIndex = list.findIndex((it) => itemId(it) === activeId);
  const newIndex = list.findIndex((it) => itemId(it) === overId);
  if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return null;
  return {
    items: { ...prev.items, [container]: arrayMove(list, oldIndex, newIndex) },
  };
};

/** コンテナごとの行の並びを比較用の文字列にする（保存要否の判定用） */
const layoutSignature = (layout: TreeLayout): string =>
  Object.keys(layout.items)
    .sort()
    .map((key) => `${key}:${layout.items[key].map(itemId).join(",")}`)
    .join("|");

// ── 行コンポーネント ─────────────────────────────────────────────────

interface SortableRowProps {
  dndId: string;
  /** ドラッグ元にできないとき true（ドロップ先・並べ替えのずれは常に有効） */
  dragDisabled: boolean;
  /** 並べ替えでこの行の上 / 下に入るときに線を出す */
  dropLine?: "top" | "bottom";
  children: React.ReactNode;
}

/** useSortable の transform を行に適用する薄いラッパー */
const SortableRow: React.FC<SortableRowProps> = ({
  dndId,
  dragDisabled,
  dropLine,
  children,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: dndId,
    // コントリビューターが自分の下書きを公開済みの行の間へ動かせるよう、
    // ドラッグできない行もドロップ先としては残す
    disabled: { draggable: dragDisabled, droppable: false },
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        // ドラッグ中の行を前面に出す
        position: "relative",
        zIndex: isDragging ? 10 : undefined,
        // dnd-kit がドラッグ元に tabIndex を付けるが、共通のフォーカスリング
        // （outline + 青い影）は出さない
        outline: "none",
        boxShadow: "none",
      }}
      {...attributes}
      {...listeners}
    >
      {children}
      {dropLine && <DropLineMark style={{ boxShadow: dropLineShadow(dropLine) }} />}
    </div>
  );
};

/** 行の上に重ねて挿入位置の線を描く（行の背景色に隠れないように） */
const DropLineMark = styled.span`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

// ── セクションブロック ───────────────────────────────────────────────

interface SectionTreeBlockProps {
  section: NoteSection;
  selectedNoteId: string | null;
  /** 表示中の下書き（変更提案）ID（下書き行のハイライト用） */
  selectedDraftId: string | null;
  selectedChangesSectionId: string | null;
  canManage: boolean;
  hideSectionHeader: boolean;
  /** 見出し行の矢印で閉じている（見出し以外を描画しない） */
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onSelectNote: (noteId: string) => void;
  /** 自分の下書き（新規作成の変更提案）行を開く */
  // eslint-disable-next-line no-unused-vars
  onSelectDraft: (changeId: string) => void;
  onOpenChanges: (section: NoteSection) => void;
  /** folder を渡すとそのフォルダ直下に作成する */
  onCreateNote: (section: NoteSection, folder?: NoteFolderSummary) => void;
  /** イメージボードを作成する（folder を渡すとそのフォルダ直下） */
  onCreateBoard: (section: NoteSection, folder?: NoteFolderSummary) => void;
  onImportNotes: (section: NoteSection) => void;
  /** セクション内の全ノート/カードを Markdown（.zip）で書き出す */
  onExportSection: (section: NoteSection) => void;
  /** フォルダ内（子孫を含む）のノート/カードを Markdown（.zip）で書き出す */
  onExportFolder: (section: NoteSection, folder: NoteFolderSummary) => void;
  onOpenSectionSettings: (section: NoteSection) => void;
  /** リンクコピーをプロジェクトスコープ URL（?tab=notes&note=）にする場合に渡す */
  linkProjectId?: string;
  /** ページ行の「編集」: そのページを開いて編集モードに入る */
  // eslint-disable-next-line no-unused-vars
  onEditNote: (noteId: string) => void;
  /** ページ行の「削除」（確認モーダルは呼び出し側） */
  // eslint-disable-next-line no-unused-vars
  onDeleteNote: (note: NoteSummary) => void;
  /** 下書き行の「下書きを破棄」（確認モーダルは呼び出し側） */
  // eslint-disable-next-line no-unused-vars
  onWithdrawDraft: (draft: NoteDraftSummary) => void;
}

/** 「移動...」で開くモーダルの対象 */
type MoveTarget =
  | { kind: "note"; note: NoteSummary }
  | { kind: "draft"; draft: NoteDraftSummary }
  | { kind: "folder"; folder: NoteFolderSummary };

/** 実行前に確認するフォルダ移動（parentId = null はセクション直下） */
interface PendingFolderMove {
  folder: NoteFolderSummary;
  parentId: string | null;
  label: string;
}

const OPEN_FOLDERS_STORAGE_KEY = "notes-open-folders";

const loadOpenFolders = (): Set<string> => {
  try {
    const raw = localStorage.getItem(OPEN_FOLDERS_STORAGE_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
};

const SectionTreeBlock: React.FC<SectionTreeBlockProps> = ({
  section,
  selectedNoteId,
  selectedDraftId,
  selectedChangesSectionId,
  canManage,
  hideSectionHeader,
  collapsed,
  onToggleCollapsed,
  onSelectNote,
  onSelectDraft,
  onOpenChanges,
  onCreateNote,
  onCreateBoard,
  onImportNotes,
  onExportSection,
  onExportFolder,
  onOpenSectionSettings,
  linkProjectId,
  onEditNote,
  onDeleteNote,
  onWithdrawDraft,
}) => {
  const [createFolder] = useCreateNoteFolderMutation();
  const [updateFolder] = useUpdateNoteFolderMutation();
  const [deleteFolder] = useDeleteNoteFolderMutation();
  const [deleteFolderFiles] = useDeleteNoteFolderFilesMutation();
  const [reorderNotes] = useReorderNotesMutation();
  const [createNote] = useCreateNoteMutation();
  const [createNoteChange] = useCreateNoteChangeMutation();
  const [updateNoteChange] = useUpdateNoteChangeMutation();
  const [initiateUpload] = useInitiateUploadMutation();
  const [fetchNote] = useLazyGetNoteQuery();
  const { copy } = useCopyToClipboard({
    successMessage: "リンクをコピーしました",
  });

  // フォルダの共有リンク（共有ライブラリと同じモーダル・仕様）
  const [shareFolder, setShareFolder] = useState<NoteFolderSummary | null>(
    null,
  );
  const [fetchShareLink] = useLazyGetNoteShareLinkQuery();
  const [saveShareLink] = useSaveNoteShareLinkMutation();
  const [deleteShareLink] = useDeleteNoteShareLinkMutation();
  const shareAdapter = useMemo<FileBrowserShareAdapter>(
    () => ({
      getLink: async (target) =>
        (await fetchShareLink({ folder_id: target.id }).unwrap()).data,
      createLink: async (target, settings) =>
        (
          await saveShareLink({
            folder_id: target.id,
            expires_at: settings.expires_at,
            require_membership: settings.require_membership,
          }).unwrap()
        ).data,
      deleteLink: async (target) => {
        await deleteShareLink({ folder_id: target.id }).unwrap();
      },
    }),
    [fetchShareLink, saveShareLink, deleteShareLink],
  );

  // D&D 中のローカル表示状態。props（ツリー再取得）が変わったら作り直す。
  // dragEnd 時に最新値から確定レイアウトを組むため ref にも持つ。
  const [layout, setLayoutState] = useState<TreeLayout>(() =>
    buildLayout(section),
  );
  const layoutRef = useRef(layout);
  const setLayout = useCallback(
    // eslint-disable-next-line no-unused-vars
    (updater: TreeLayout | ((prev: TreeLayout) => TreeLayout)) => {
      setLayoutState((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        layoutRef.current = next;
        return next;
      });
    },
    [],
  );
  useEffect(() => {
    setLayout(buildLayout(section));
  }, [section, setLayout]);

  // フォルダのみのツリー（メニューの件数集計・循環チェック用）
  const folderTree = useMemo(() => {
    const build = (parentKey: string): FolderNode[] =>
      (layout.items[parentKey] ?? [])
        .filter(
          (it): it is Extract<TreeItem, { kind: "folder" }> =>
            it.kind === "folder",
        )
        .map((it) => ({
          folder: it.folder,
          children: build(it.folder.id),
        }));
    return build(ROOT);
  }, [layout]);
  const folderNodes = useMemo(() => flattenNodes(folderTree), [folderTree]);
  const folderNodeById = useMemo(
    () => new Map(folderNodes.map((node) => [node.folder.id, node])),
    [folderNodes],
  );

  /** 指定フォルダ自身と全子孫フォルダの ID 集合（循環チェック・集計用） */
  const descendantIdsOf = useCallback(
    (folderId: string): Set<string> => {
      const node = folderNodeById.get(folderId);
      if (!node) return new Set([folderId]);
      return new Set(flattenNodes([node]).map((n) => n.folder.id));
    },
    [folderNodeById],
  );
  // collisionDetection はドラッグ中に毎フレーム呼ばれるため安定参照にし、
  // 最新のツリーは ref 経由で読む
  const descendantIdsRef = useRef(descendantIdsOf);
  useEffect(() => {
    descendantIdsRef.current = descendantIdsOf;
  }, [descendantIdsOf]);

  const [openFolders, setOpenFolders] = useState<Set<string>>(loadOpenFolders);
  const toggleFolder = useCallback((folderId: string) => {
    setOpenFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      try {
        localStorage.setItem(
          OPEN_FOLDERS_STORAGE_KEY,
          JSON.stringify([...next]),
        );
      } catch {
        /* localStorage が使えない環境では開閉状態を永続化しない */
      }
      return next;
    });
  }, []);

  const [renamingFolderId, setRenamingFolderId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [deletingFolder, setDeletingFolder] =
    useState<NoteFolderSummary | null>(null);
  const [purgingFolder, setPurgingFolder] = useState<NoteFolderSummary | null>(
    null,
  );
  const [moveTarget, setMoveTarget] = useState<MoveTarget | null>(null);
  // イメージボードのリネーム（本文の編集画面を持たないため専用モーダル）
  const [renamingNote, setRenamingNote] = useState<NoteSummary | null>(null);
  const [updateNote, { isLoading: renamingNoteSaving }] = useUpdateNoteMutation();
  const commitNoteRename = useCallback(
    async (title: string) => {
      if (!renamingNote) return;
      try {
        await updateNote({ note_id: renamingNote.id, title }).unwrap();
        setRenamingNote(null);
      } catch {
        toast.error("名前を変更できませんでした");
      }
    },
    [renamingNote, updateNote],
  );
  const [pendingFolderMove, setPendingFolderMove] =
    useState<PendingFolderMove | null>(null);
  const [movingFolder, setMovingFolder] = useState(false);
  const renameInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (renamingFolderId) renameInputRef.current?.select();
  }, [renamingFolderId]);

  // クリック（選択）と D&D を両立させるため、少し動かしてから発火する
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  // 公開済みの行（フォルダ / ノート）の並べ替え・移動はメンテナー以上。
  // 自分の下書きはコントリビューターでも動かせる（配置を下書きに保存する）
  const dndDisabled = !section.can_edit;
  const draftDragDisabled = !section.can_contribute;

  /** フォルダ（null = セクション直下）が承認不要エリア（祖先を含む）か */
  const folderSkips = useCallback(
    (folderId: string | null | undefined) =>
      !!folderId &&
      !!section.folders.find((f) => f.id === folderId)?.effective_skip_review,
    [section.folders],
  );
  /**
   * 承認不要エリアの中から外へ出す移動か（直接はできず、移動のプルリク
   * エストが必要）。承認者が自分だけ（can_direct_update）なら直接できる。
   * 承認不要フォルダで公開してから外へ出す抜け道を塞ぐ（BE も同条件で 403）
   */
  const exitNeedsReview = useCallback(
    (from: string | null | undefined, to: string | null | undefined) =>
      !section.can_direct_update && folderSkips(from) && !folderSkips(to),
    [section.can_direct_update, folderSkips],
  );
  /**
   * イメージボードを作成・リネームできるか。中身は閲覧者が直接編集するため、
   * 削除・並べ替えと同じくメンテナー以上の直接操作としても許可する。ほかに
   * ノートを PR なしで作成できる人（承認者が自分だけ / 承認不要フォルダ内の
   * コントリビューター）も可（BE の can_manage_board_page と同条件）
   */
  const canManageBoardIn = useCallback(
    (folderId: string | null | undefined) =>
      section.can_edit ||
      section.can_direct_update ||
      (section.can_contribute && folderSkips(folderId)),
    [section.can_edit, section.can_direct_update, section.can_contribute, folderSkips],
  );

  /**
   * 行（ノート / フォルダ）のドラッグ共通のカスタム collision detection。
   * 判定そのものは pickTreeDropTarget（純粋関数）に任せ、ここでは
   * dnd-kit の droppable 矩形を行の一覧に変換するだけにする。
   *
   * - フォルダ行の droppable（folder:）は行だけの矩形なのでそのまま使う。
   *   フォルダの sortable（folderdrag:）は行 + 開いた中身の矩形で大きく、
   *   判定に混ぜると末尾のフォルダに吸い込まれるため使わない
   * - フォルダのドラッグ中は、自分自身と子孫（とその中のページ）を除外する
   *   （中へは移動できないため）
   * - どの行の上でもなければ何も返さない（最寄りの行へ寄せない）
   */
  const collisionDetection = useCallback<CollisionDetection>((args) => {
    const pointer = args.pointerCoordinates;
    if (!pointer) return [];
    const activeInfo = parseDndId(String(args.active.id));
    const layout = layoutRef.current;
    const excluded =
      activeInfo.kind === "folderdrag"
        ? descendantIdsRef.current(activeInfo.id)
        : null;

    let rootBar: { top: number; bottom: number } | null = null;
    const folderRows: TreeRow[] = [];
    const itemRows: TreeRow[] = [];
    for (const container of args.droppableContainers) {
      const rect = args.droppableRects.get(container.id);
      if (!rect) continue;
      const info = parseDndId(String(container.id));
      if (info.kind === "rootzone") {
        rootBar = rect;
      } else if (info.kind === "folder") {
        if (excluded?.has(info.id)) continue;
        folderRows.push({ id: info.id, rect });
      } else if (info.kind === "note" || info.kind === "draft") {
        if (excluded) {
          const parent = findContainer(layout, info.id);
          if (parent && excluded.has(parent)) continue;
        }
        itemRows.push({ id: String(container.id), rect });
      }
    }

    const target = pickTreeDropTarget({
      pointerY: pointer.y,
      rootBar,
      folderRows,
      itemRows,
    });
    return target ? [{ id: target }] : [];
  }, []);

  // ドラッグ中の行情報（DragOverlay のゴースト表示とルートドロップ導線用）
  const [activeDrag, setActiveDrag] = useState<{
    kind: "note" | "draft" | "folder";
    label: string;
    icon: string;
  } | null>(null);

  // ── フォルダ CRUD ────────────────────────────────────────────────

  const handleAddFolder = useCallback(
    async (parent?: NoteFolderSummary) => {
      try {
        const result = await createFolder({
          section_id: section.id,
          title: "新しいフォルダ",
          ...(parent ? { parent_id: parent.id } : {}),
        }).unwrap();
        // 追加直後にリネームモードへ（親ごと開いた状態にしておく）
        const folder = result.data.folder;
        setOpenFolders((prev) => {
          const next = new Set(prev).add(folder.id);
          if (parent) next.add(parent.id);
          return next;
        });
        setRenamingFolderId(folder.id);
        setRenameValue(folder.title);
      } catch {
        toast.error("フォルダの作成に失敗しました");
      }
    },
    [createFolder, section.id],
  );

  const commitRename = useCallback(async () => {
    const folderId = renamingFolderId;
    const title = renameValue.trim();
    setRenamingFolderId(null);
    if (!folderId || !title) return;
    const current = section.folders.find((f) => f.id === folderId);
    if (!current || current.title === title) return;
    try {
      await updateFolder({ folder_id: folderId, title }).unwrap();
    } catch {
      toast.error("フォルダ名の変更に失敗しました");
    }
  }, [renamingFolderId, renameValue, section.folders, updateFolder]);

  const handleDeleteFolder = useCallback(async () => {
    if (!deletingFolder) return;
    try {
      await deleteFolder({ folder_id: deletingFolder.id }).unwrap();
      setDeletingFolder(null);
    } catch {
      toast.error("フォルダの削除に失敗しました");
    }
  }, [deletingFolder, deleteFolder]);

  // 全ファイル削除（フォルダ構造は残し、子孫を含む中のノート/カードを削除）
  const handlePurgeFolder = useCallback(async () => {
    if (!purgingFolder) return;
    try {
      await deleteFolderFiles({ folder_id: purgingFolder.id }).unwrap();
      setPurgingFolder(null);
      toast.success("フォルダ内のファイルを削除しました");
    } catch {
      toast.error("ファイルの削除に失敗しました");
    }
  }, [purgingFolder, deleteFolderFiles]);

  /**
   * フォルダを別の親（null = セクション直下）へ移動する前の確認を開く。
   * 配下ごと動くため、ドラッグでも「移動...」でも必ず確認を挟む
   * （共有ライブラリと同じ。行全体がドラッグ元なので誤操作の最後の歯止め）。
   */
  const requestFolderMove = useCallback(
    (folderId: string, parentId: string | null) => {
      const folder = section.folders.find((f) => f.id === folderId);
      if (!folder) return;
      if ((folder.parent_id ?? null) === parentId) return;
      // 自分自身・子孫への移動（循環）は受け付けない
      if (parentId && descendantIdsOf(folderId).has(parentId)) return;
      // 承認不要を親から受け継いでいるフォルダは、外へ出すと中身がレビュー
      // なしで通常の場所へ出てしまうため動かせない（自身が承認不要の
      // フォルダはどこへ動かしても承認不要のまま）
      if (
        !section.can_direct_update &&
        folder.effective_skip_review &&
        !folder.skip_review &&
        !folderSkips(parentId)
      ) {
        toast.error(
          "承認不要フォルダの中のフォルダは外へ移動できません。中のページは移動のプルリクエストで移動してください",
        );
        return;
      }
      const label = parentId
        ? (section.folders.find((f) => f.id === parentId)?.title ?? "")
        : "セクション直下";
      setPendingFolderMove({ folder, parentId, label });
    },
    [section.folders, section.can_direct_update, descendantIdsOf, folderSkips],
  );

  const confirmFolderMove = useCallback(async () => {
    if (!pendingFolderMove || movingFolder) return;
    const { folder, parentId } = pendingFolderMove;
    setMovingFolder(true);
    try {
      await updateFolder({
        folder_id: folder.id,
        parent_id: parentId,
      }).unwrap();
      // 移動先を開いて結果を見せる
      if (parentId) {
        setOpenFolders((prev) => new Set(prev).add(parentId));
      }
      setPendingFolderMove(null);
    } catch {
      toast.error("フォルダの移動に失敗しました");
    } finally {
      setMovingFolder(false);
    }
  }, [pendingFolderMove, movingFolder, updateFolder]);

  // ── OS ファイル D&D 取り込み（.md / .txt をノート化） ─────────────

  // 直接更新権限があれば（または承認不要フォルダへのドロップなら）ノートを
  // 直接作成、無くてもコントリビューター以上なら下書き（変更提案）として
  // 取り込める（後からプルリクエストで提出する）
  const importsAsDraftInto = useCallback(
    (folder: NoteFolderSummary | null) =>
      !section.can_direct_update &&
      !(section.can_contribute && folder?.effective_skip_review),
    [section.can_direct_update, section.can_contribute],
  );
  const canDropFiles = section.can_contribute;
  const importingRef = useRef(false);
  const [nativeOverSection, setNativeOverSection] = useState(false);
  // dragenter/dragleave は子要素間の移動でも発火するため深さを数える
  const nativeDragDepthRef = useRef(0);

  /**
   * ドロップされたファイル群をノートとして取り込む。
   * folder 指定時はそのフォルダ直下へ、フォルダごとのドロップは
   * relativePath の階層をフォルダとして再現する（作成はメンテナー以上）。
   * 直接作成できない場合は下書き（変更提案）として取り込み、後から
   * プルリクエストで提出できるようにする（ドロップしたフォルダを配置先に
   * 持つ。階層は再現しない）。
   */
  const handleNativeFileDrop = useCallback(
    async (folder: NoteFolderSummary | null, entries: FileWithPath[]) => {
      if (importingRef.current) return;
      importingRef.current = true;
      const importsAsDraft = importsAsDraftInto(folder);
      const toastId = toast.loading(
        importsAsDraft ? "下書きとして取り込み中..." : "ノートを取り込み中...",
      );
      try {
        const result = await importNoteEntries(entries, {
          sectionId: section.id,
          baseFolderId: folder?.id,
          existingFolders: section.folders,
          initiateUpload,
          createNote: importsAsDraft
            ? (params) =>
                createNoteChange({
                  target_type: "note",
                  section_id: params.section_id,
                  payload: {
                    title: params.title,
                    content: params.content ?? "",
                    ...(params.folder_id
                      ? { folder_id: params.folder_id }
                      : {}),
                  },
                }).unwrap()
            : (params) => createNote(params).unwrap(),
          // フォルダ作成はメンテナー以上の直接操作のみ。下書き取り込みは
          // フォルダを作れないため、ドロップ先フォルダへフラットに取り込む
          createFolder:
            !importsAsDraft && section.can_edit
              ? async (params) =>
                  (await createFolder(params).unwrap()).data.folder
              : undefined,
        });
        if (result.noteCount === 0) {
          toast.error(
            "ノートにできるファイル（.md / .markdown / .txt）がありません",
            { id: toastId },
          );
          return;
        }
        // ドロップ先フォルダを開いて結果を見せる
        if (folder) {
          setOpenFolders((prev) => new Set(prev).add(folder.id));
        }
        const skipped =
          result.skippedCount > 0
            ? `（非対応の${result.skippedCount}件はスキップ）`
            : "";
        toast.success(
          importsAsDraft
            ? `${result.noteCount}件を下書きとして取り込みました${skipped}。ツリーの「下書き」行から編集・提出できます`
            : `${result.noteCount}件のノートを取り込みました${skipped}`,
          {
            id: toastId,
          },
        );
      } catch {
        toast.error("ノートの取り込みに失敗しました", { id: toastId });
      } finally {
        importingRef.current = false;
      }
    },
    [
      section,
      importsAsDraftInto,
      initiateUpload,
      createNote,
      createNoteChange,
      createFolder,
    ],
  );

  /**
   * セクション全体（SectionBlock）の外部ファイルドロップ受け。
   * フォルダ行上のドロップはフォルダ側で preventDefault 済みのため、
   * ここではハイライト解除のみ行い二重取り込みを防ぐ。
   */
  const sectionNativeDropHandlers = canDropFiles
    ? {
        onDragEnter: (e: React.DragEvent<HTMLDivElement>) => {
          if (!containsNativeFiles(e)) return;
          e.preventDefault();
          nativeDragDepthRef.current += 1;
          setNativeOverSection(true);
        },
        onDragOver: (e: React.DragEvent<HTMLDivElement>) => {
          if (!containsNativeFiles(e)) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = "copy";
        },
        onDragLeave: (e: React.DragEvent<HTMLDivElement>) => {
          if (!containsNativeFiles(e)) return;
          nativeDragDepthRef.current = Math.max(
            0,
            nativeDragDepthRef.current - 1,
          );
          if (nativeDragDepthRef.current === 0) setNativeOverSection(false);
        },
        onDrop: (e: React.DragEvent<HTMLDivElement>) => {
          if (!containsNativeFiles(e)) return;
          nativeDragDepthRef.current = 0;
          setNativeOverSection(false);
          // フォルダ行が処理済み（preventDefault 済み）なら何もしない
          if (e.defaultPrevented) return;
          e.preventDefault();
          void extractDropEntries(e).then((entries) => {
            if (entries.length > 0) void handleNativeFileDrop(null, entries);
          });
        },
      }
    : {};

  /** フォルダ内のノート/カード数（子孫フォルダを含む。メニュー・確認文言用） */
  const countFolderItems = useCallback(
    (folderId: string) => {
      const ids = descendantIdsOf(folderId);
      const contained = section.notes.filter(
        (n) => n.folder_id && ids.has(n.folder_id),
      );
      const cards = contained.filter((n) => n.display_style === "card").length;
      const boards = contained.filter((n) => n.display_style === "board").length;
      return { notes: contained.length - cards - boards, cards, boards };
    },
    [section.notes, descendantIdsOf],
  );

  /**
   * セクション見出し行の3点リーダーメニュー（作成系の集約）。
   * ノート作成はコントリビューター以上、フォルダ作成はメンテナー以上。
   * 作成は常に「ノート」から始め、カードへの変更はエディタ内トグル /
   * 閲覧メニューの「カード表示に切り替え」で行うフローに統一している。
   */
  const sectionMenuItems = useMemo((): DropdownMenuItem[] => {
    const items: DropdownMenuItem[] = [];
    if (section.can_contribute) {
      items.push({
        label: "ノートを作成",
        icon: "add",
        onClick: () => onCreateNote(section),
      });
    }
    if (canManageBoardIn(null)) {
      items.push({
        label: "イメージボードを作成",
        icon: "imagesmode",
        onClick: () => onCreateBoard(section),
      });
    }
    if (section.can_edit) {
      items.push({
        label: "フォルダを作成",
        icon: "create_new_folder",
        onClick: () => void handleAddFolder(),
      });
    }
    return items;
  }, [section, onCreateNote, onCreateBoard, canManageBoardIn, handleAddFolder]);

  /** フォルダの「プルリク承認不要」を切り替える（子孫フォルダにも効く） */
  const toggleSkipReview = useCallback(
    async (folder: NoteFolderSummary) => {
      const next = !folder.skip_review;
      try {
        await updateFolder({
          folder_id: folder.id,
          skip_review: next,
        }).unwrap();
        toast.success(
          next
            ? `「${folder.title}」を承認不要にしました。中のノートはプルリクエストなしで追加・更新できます`
            : `「${folder.title}」の承認不要を解除しました`,
        );
      } catch {
        toast.error("承認不要の設定を変更できませんでした");
      }
    },
    [updateFolder],
  );

  /**
   * フォルダ行の3点リーダーメニュー。並びは共有ライブラリのフォルダ行に揃える
   * （一括ダウンロード → 作成 → リネーム → 移動 → 削除）。
   * フォルダ内への作成はコントリビューター以上（直接更新できないセクション
   * では下書き/PR として作成され、マージ時にこのフォルダへ配置される）。
   * 一括ダウンロードは閲覧できる人なら誰でも、それ以外の管理操作は
   * メンテナー以上。
   */
  const buildFolderMenuItems = useCallback(
    (folder: NoteFolderSummary): DropdownMenuItem[] => {
      const { notes, cards, boards } = countFolderItems(folder.id);
      const items: DropdownMenuItem[] = [];
      if (notes + cards > 0) {
        items.push({
          label: "一括ダウンロード",
          icon: "download",
          onClick: () => onExportFolder(section, folder),
        });
      }
      // 共有リンク（中のノートを外部へ閲覧専用で公開）はメンテナー以上
      if (section.can_edit) {
        items.push({
          label: "共有",
          icon: "share",
          onClick: () => setShareFolder(folder),
        });
      }
      if (section.can_contribute) {
        items.push({
          label: "ノートを作成",
          icon: "add",
          onClick: () => onCreateNote(section, folder),
        });
      }
      if (canManageBoardIn(folder.id)) {
        items.push({
          label: "イメージボードを作成",
          icon: "imagesmode",
          onClick: () => onCreateBoard(section, folder),
        });
      }
      if (section.can_edit) {
        items.push({
          label: "フォルダを作成",
          icon: "create_new_folder",
          onClick: () => void handleAddFolder(folder),
        });
        items.push({
          label: "リネーム",
          icon: "edit",
          onClick: () => {
            setRenamingFolderId(folder.id);
            setRenameValue(folder.title);
          },
        });
        items.push({
          label: "移動...",
          icon: "drive_file_move",
          onClick: () => setMoveTarget({ kind: "folder", folder }),
        });
        // 承認不要はレビューを外す設定のため、オーナーだけが切り替えられる
        // （BE も同じ条件で 403）
        if (section.my_role === "owner") {
          items.push({
            label: folder.skip_review ? "承認不要を解除" : "承認不要にする",
            icon: folder.skip_review ? "lock" : "lock_open",
            onClick: () => void toggleSkipReview(folder),
          });
        }
        items.push({
          label: "フォルダ削除",
          icon: "delete",
          onClick: () => {
            // 承認不要フォルダを消すと中身が親（承認不要の外）へ戻ってしまう
            // ため、中身があるうちは消せない（BE も同条件で 403）
            const releasesContent =
              section.notes.some((n) => n.folder_id === folder.id) ||
              section.folders.some(
                (f) => f.parent_id === folder.id && !f.skip_review,
              );
            if (
              exitNeedsReview(folder.id, folder.parent_id) &&
              releasesContent
            ) {
              toast.error(
                "承認不要フォルダは中身を空にしてから削除してください（中のページは移動のプルリクエストで移動できます）",
              );
              return;
            }
            setDeletingFolder(folder);
          },
        });
        if (notes + cards + boards > 0) {
          items.push({
            label: "全ファイル削除",
            icon: "delete_sweep",
            onClick: () => setPurgingFolder(folder),
          });
        }
      }
      return items;
    },
    [
      section,
      countFolderItems,
      onCreateNote,
      onCreateBoard,
      canManageBoardIn,
      handleAddFolder,
      onExportFolder,
      toggleSkipReview,
      exitNeedsReview,
    ],
  );

  /** ページ 1 件を Markdown でダウンロードする（本文はツリーに無いため取得する） */
  const downloadNote = useCallback(
    async (note: NoteSummary) => {
      try {
        const res = await fetchNote({ note_id: note.id }, true).unwrap();
        downloadNoteAsMarkdown(res.data.note.title, res.data.note.content);
      } catch {
        toast.error("ダウンロードに失敗しました");
      }
    },
    [fetchNote],
  );

  /**
   * ページ行の3点リーダーメニュー。並びは共有ライブラリのファイル行に揃える
   * （ダウンロード → リンク → 編集 → 移動 → 削除）。
   * ダウンロード / リンクコピーは閲覧できる人なら誰でも、編集は
   * コントリビューター以上（PR 経由を含む）、移動・削除はメンテナー以上。
   */
  const buildNoteMenuItems = useCallback(
    (note: NoteSummary): DropdownMenuItem[] => {
      // イメージボードは Markdown 本文を持たないため、ダウンロードと編集の
      // 代わりにリネームだけを出す
      const isBoard = note.display_style === "board";
      const items: DropdownMenuItem[] = [];
      if (!isBoard) {
        items.push({
          label: "ダウンロード",
          icon: "download",
          onClick: () => void downloadNote(note),
        });
      }
      items.push({
        label: "リンクをコピー",
        icon: "link",
        onClick: () => {
          void copy(
            toMarkdownLink(note.title, buildNoteLink(note.id, linkProjectId)),
          );
        },
      });
      if (isBoard && canManageBoardIn(note.folder_id)) {
        items.push({
          label: "リネーム",
          icon: "edit",
          onClick: () => setRenamingNote(note),
        });
      }
      if (section.can_contribute) {
        if (!isBoard) {
          items.push({
            label: "編集",
            icon: "edit",
            onClick: () => onEditNote(note.id),
          });
        }
        // メンテナー以上は直接移動（承認不要の外へ出すときは移動の
        // プルリクエスト）、コントリビューターは常に移動のプルリクエスト
        items.push({
          label: "移動...",
          icon: "drive_file_move",
          onClick: () => setMoveTarget({ kind: "note", note }),
        });
      }
      if (section.can_edit) {
        items.push({
          label: "削除",
          icon: "delete",
          onClick: () => onDeleteNote(note),
        });
      }
      return items;
    },
    [
      section,
      downloadNote,
      copy,
      linkProjectId,
      canManageBoardIn,
      onEditNote,
      onDeleteNote,
    ],
  );

  /**
   * 下書き行の3点リーダーメニュー（下書きは本人にしか返らない）。
   * 「移動...」は下書きの配置（マージ時の作成先）を変える
   */
  const buildDraftMenuItems = useCallback(
    (draft: NoteDraftSummary): DropdownMenuItem[] => [
      {
        label: "移動...",
        icon: "drive_file_move",
        onClick: () => setMoveTarget({ kind: "draft", draft }),
      },
      {
        label: "下書きを破棄",
        icon: "delete",
        onClick: () => onWithdrawDraft(draft),
      },
    ],
    [onWithdrawDraft],
  );

  /**
   * 下書きの配置（フォルダと並び位置）を保存する。publish=true なら、
   * 承認不要フォルダへの移動と同時にレビューなしで公開する
   */
  const commitDraftPlacement = useCallback(
    async (
      draft: NoteDraftSummary,
      folderId: string | null,
      sortOrder: number | null,
      publish: boolean,
    ) => {
      try {
        const result = await updateNoteChange({
          change_id: draft.id,
          placement: { folder_id: folderId, sort_order: sortOrder },
          ...(publish ? { publish: true } : {}),
        }).unwrap();
        const published = result.data.target;
        if (published) {
          toast.success(`「${draft.title}」を公開しました`);
          // 開いていた下書きは公開したページへ切り替える
          if (draft.id === selectedDraftId) onSelectNote(published.id);
        }
      } catch {
        toast.error(
          publish
            ? "下書きを公開できませんでした"
            : "下書きの移動を保存できませんでした",
        );
        setLayout(buildLayout(section));
      }
    },
    [updateNoteChange, section, setLayout, selectedDraftId, onSelectNote],
  );

  // 承認不要フォルダの外へ動かそうとしたページの「移動のプルリクエスト」確認
  const [pendingMovePr, setPendingMovePr] = useState<{
    note: NoteSummary;
    folderId: string | null;
    sortOrder: number | null;
  } | null>(null);
  const [sendingMovePr, setSendingMovePr] = useState(false);

  const folderLabel = useCallback(
    (folderId: string | null) =>
      folderId
        ? (section.folders.find((f) => f.id === folderId)?.title ?? "")
        : "セクション直下",
    [section.folders],
  );

  /** 移動の提案（プルリクエスト）を送る。承認されたら移動する */
  const sendMovePr = useCallback(async () => {
    if (!pendingMovePr || sendingMovePr) return;
    const { note, folderId, sortOrder } = pendingMovePr;
    setSendingMovePr(true);
    try {
      await createNoteChange({
        target_type: note.display_style === "card" ? "card" : "note",
        target_id: note.id,
        move: { folder_id: folderId, sort_order: sortOrder },
        description: `「${note.title}」を「${folderLabel(folderId)}」へ移動`,
        submit: true,
      }).unwrap();
      toast.success(
        "移動のプルリクエストを送信しました。承認されると移動します",
      );
      setPendingMovePr(null);
    } catch {
      toast.error("移動のプルリクエストを送信できませんでした");
    } finally {
      setSendingMovePr(false);
    }
  }, [pendingMovePr, sendingMovePr, createNoteChange, folderLabel]);

  // 承認不要フォルダへ動かした下書きの「このまま公開しますか？」確認
  const [pendingPublish, setPendingPublish] = useState<{
    draft: NoteDraftSummary;
    folder: NoteFolderSummary;
    sortOrder: number | null;
  } | null>(null);

  /**
   * 下書きの配置を保存する。承認不要フォルダ（祖先を含む）の外から中へ
   * 動かしたときは、そのまま公開するかを確認してから保存する（書きかけの
   * 下書きを黙って公開しないため。承認不要フォルダ内での並べ替えでは
   * 毎回は聞かない）
   */
  const saveDraftPlacement = useCallback(
    async (
      draft: NoteDraftSummary,
      folderId: string | null,
      sortOrder: number | null,
    ) => {
      if (
        (draft.folder_id ?? null) === folderId &&
        (draft.sort_order ?? null) === sortOrder
      ) {
        return;
      }
      const findFolder = (id: string | null) =>
        id ? section.folders.find((f) => f.id === id) : undefined;
      const folder = findFolder(folderId);
      const enteringReviewFree =
        !!folder?.effective_skip_review &&
        !findFolder(draft.folder_id)?.effective_skip_review;
      if (folder && enteringReviewFree && section.can_contribute) {
        setPendingPublish({ draft, folder, sortOrder });
        return;
      }
      await commitDraftPlacement(draft, folderId, sortOrder, false);
    },
    [section.folders, section.can_contribute, commitDraftPlacement],
  );

  const resolvePendingPublish = useCallback(
    (publish: boolean) => {
      if (!pendingPublish) return;
      const { draft, folder, sortOrder } = pendingPublish;
      setPendingPublish(null);
      void commitDraftPlacement(draft, folder.id, sortOrder, publish);
    },
    [pendingPublish, commitDraftPlacement],
  );

  // ── D&D ──────────────────────────────────────────────────────────

  // 「中に入れる」（folder:）とセクション直下バー（rootzone）は、移動先の上に
  // DROP_DWELL_MS 留まってから離したときだけ確定する（共有ライブラリと同じ）
  const { resetDropDwell, handleDropDwellOver, isDropDwellSatisfied } =
    useDropDwellGate();
  // 並べ替えで入る位置の線（行はずらさず線で示す）
  const [dropLine, setDropLine] = useState<DropLine>(null);

  /**
   * 行（ノート / 下書き / フォルダ）の上に来たら、ドラッグ中のノート・下書きを
   * その行のコンテナへ随時ローカル状態で移す（並び替えのプレビュー）。
   * フォルダの「中に入れる」・セクション直下バー・フォルダのドラッグ
   * （階層移動）は滞留の判定が要るため dragEnd でのみ確定する。
   */
  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      handleDropDwellOver(event);
      const { active, over } = event;
      const activeInfo = parseDndId(String(active.id));
      const overInfo = over ? parseDndId(String(over.id)) : null;
      if (
        !overInfo ||
        overInfo.id === activeInfo.id ||
        (overInfo.kind !== "note" &&
          overInfo.kind !== "draft" &&
          overInfo.kind !== "folderdrag")
      ) {
        setDropLine(null);
        return;
      }
      // 行の上 = 並べ替え。dragEnd と同じ規則で入る位置に線を出す
      // （同じコンテナなら元の位置との前後で上 / 下、別のコンテナならその
      // 行の手前）。フォルダを別コンテナの行へ落とすとそのコンテナの末尾に
      // 入るため線は出さない
      const layout = layoutRef.current;
      const from = findContainer(layout, activeInfo.id);
      const to = findContainer(layout, overInfo.id);
      if (!from || !to) {
        setDropLine(null);
        return;
      }
      if (from === to) {
        const list = layout.items[from];
        const activeIndex = list.findIndex((it) => itemId(it) === activeInfo.id);
        const overIndex = list.findIndex((it) => itemId(it) === overInfo.id);
        setDropLine({
          id: overInfo.id,
          edge: activeIndex < overIndex ? "bottom" : "top",
        });
        return;
      }
      setDropLine(
        activeInfo.kind === "folderdrag"
          ? null
          : { id: overInfo.id, edge: "top" },
      );
    },
    [handleDropDwellOver],
  );

  /**
   * レイアウト全体（フォルダ・ノート混在の並び順とノートの所属フォルダ）を
   * コンテナごとの連番で一括保存する（失敗時はサーバー順へ再構築）。
   * フォルダの親変更は requestFolderMove（PATCH）経由のためここでは送らない。
   * 下書きは公開済みの行ではないため保存対象から外す（連番にも数えない）。
   */
  const commitLayout = useCallback(
    async (finalLayout: TreeLayout) => {
      const items: ReorderItem[] = [];
      for (const [container, list] of Object.entries(finalLayout.items)) {
        list
          .filter((it) => it.kind !== "draft")
          .forEach((it, index) => {
          if (it.kind === "note") {
            items.push({
              type: "note",
              id: it.note.id,
              folder_id: container === ROOT ? null : container,
              sort_order: index,
            });
          } else if (it.kind === "folder") {
            items.push({
              type: "folder",
              id: it.folder.id,
              sort_order: index,
            });
          }
        });
      }
      if (items.length === 0) return;
      try {
        await reorderNotes({ section_id: section.id, items }).unwrap();
      } catch {
        toast.error("並べ替えの保存に失敗しました");
        setLayout(buildLayout(section));
      }
    },
    [reorderNotes, section, setLayout],
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      resetDropDwell();
      setDropLine(null);
      const info = parseDndId(String(event.active.id));
      if (info.kind === "note") {
        const note = section.notes.find((n) => n.id === info.id);
        if (note) {
          setActiveDrag({
            kind: "note",
            label: note.title,
            icon: pageIcon(note.display_style),
          });
        }
      } else if (info.kind === "draft") {
        const draft = section.my_drafts.find((d) => d.id === info.id);
        if (draft) {
          setActiveDrag({
            kind: "draft",
            label: draft.title,
            icon: "edit_note",
          });
        }
      } else if (info.kind === "folderdrag") {
        const folder = section.folders.find((f) => f.id === info.id);
        if (folder) {
          setActiveDrag({
            kind: "folder",
            label: folder.title,
            icon: "folder",
          });
        }
      }
    },
    [section, resetDropDwell],
  );

  /** ドラッグ中のプレビューを捨ててサーバーの並びに戻す */
  const revertLayout = useCallback(() => {
    setLayout(buildLayout(section));
  }, [section, setLayout]);

  const handleDragCancel = useCallback(() => {
    setActiveDrag(null);
    setDropLine(null);
    revertLayout();
  }, [revertLayout]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveDrag(null);
      setDropLine(null);
      const { active, over } = event;
      const activeInfo = parseDndId(String(active.id));
      const overInfo = over ? parseDndId(String(over.id)) : null;
      // 「中に入れる」とセクション直下バーは、青くなる前に離したら移動しない
      // （移動しないことは見た目で伝わっているので黙って元に戻す）
      const needsDwell =
        overInfo?.kind === "folder" || overInfo?.kind === "rootzone";
      if (!overInfo || (needsDwell && !isDropDwellSatisfied(event))) {
        revertLayout();
        return;
      }

      // フォルダのドラッグ = 同一コンテナの並び替え or 親フォルダの付け替え
      if (activeInfo.kind === "folderdrag") {
        const prev = layoutRef.current;
        if (overInfo.kind === "folder") {
          requestFolderMove(activeInfo.id, overInfo.id);
          return;
        }
        if (overInfo.kind === "rootzone") {
          requestFolderMove(activeInfo.id, null);
          return;
        }
        const container = findContainer(prev, activeInfo.id);
        const overContainer = findContainer(prev, overInfo.id);
        if (!container || !overContainer) return;
        if (container === overContainer) {
          // 同一コンテナ = 並び替え（ノートとの混在も自由）
          const next = reorderWithin(
            prev,
            container,
            activeInfo.id,
            overInfo.id,
          );
          if (next) {
            setLayout(next);
            void commitLayout(next);
          }
          return;
        }
        // 別コンテナの行の上で離したら、その行が属するコンテナへ移動
        requestFolderMove(
          activeInfo.id,
          overContainer === ROOT ? null : overContainer,
        );
        return;
      }

      if (activeInfo.kind !== "note" && activeInfo.kind !== "draft") return;

      const prev = layoutRef.current;
      let next = prev;
      if (overInfo.kind === "folder") {
        next = moveToContainerEnd(prev, activeInfo.id, overInfo.id);
      } else if (overInfo.kind === "rootzone") {
        next = moveToContainerEnd(prev, activeInfo.id, ROOT);
      } else {
        // 行の上: 同一コンテナなら並び替え、別コンテナならその行の手前へ
        // （dragOver の線の位置と同じ規則）
        const container = findContainer(prev, activeInfo.id);
        const overContainer = findContainer(prev, overInfo.id);
        if (container && container === overContainer) {
          next =
            reorderWithin(prev, container, activeInfo.id, overInfo.id) ?? prev;
        } else if (overContainer) {
          next = moveAcrossContainers(
            prev,
            activeInfo.id,
            overContainer,
            overInfo.id,
          );
        }
      }

      // 承認不要フォルダの外へ出す移動は直接できないため、並びは元に戻して
      // 「移動のプルリクエスト」を送るか確認する
      if (activeInfo.kind === "note") {
        const note = section.notes.find((n) => n.id === activeInfo.id);
        const container = findContainer(next, activeInfo.id);
        const toFolder = container === ROOT ? null : container;
        if (note && container && exitNeedsReview(note.folder_id, toFolder)) {
          revertLayout();
          setPendingMovePr({
            note,
            folderId: toFolder,
            // 動かした行自身は並び位置の基準にしない
            sortOrder: draftSortOrderAt(
              next.items[container].map((it) => ({
                isDraft: it.kind === "draft" || itemId(it) === note.id,
                id: itemId(it),
                sortOrder: it.kind === "draft" ? 0 : itemSortOrder(it),
              })),
              note.id,
            ),
          });
          return;
        }
      }

      setLayout(next);

      // 下書きは公開済みの並びを変えず、自分の配置（フォルダと並び位置）だけ
      // 下書きに保存する（マージ時にその位置へ作成される）
      if (activeInfo.kind === "draft") {
        const draft = section.my_drafts.find((d) => d.id === activeInfo.id);
        const container = findContainer(next, activeInfo.id);
        if (!draft || !container) return;
        void saveDraftPlacement(
          draft,
          container === ROOT ? null : container,
          draftSortOrderAt(
            next.items[container].map((it) => ({
              isDraft: it.kind === "draft",
              id: itemId(it),
              sortOrder: it.kind === "draft" ? 0 : itemSortOrder(it),
            })),
            activeInfo.id,
          ),
        );
        return;
      }

      // 元の位置に落としただけなら保存しない
      if (layoutSignature(next) !== layoutSignature(buildLayout(section))) {
        void commitLayout(next);
      }
    },
    [
      commitLayout,
      saveDraftPlacement,
      setLayout,
      revertLayout,
      requestFolderMove,
      isDropDwellSatisfied,
      exitNeedsReview,
      section,
    ],
  );

  /** 「移動...」モーダルの確定 */
  const handleMoveTo = useCallback(
    (folderId: string | null) => {
      if (!moveTarget) return;
      setMoveTarget(null);
      if (moveTarget.kind === "folder") {
        requestFolderMove(moveTarget.folder.id, folderId);
        return;
      }
      if (
        moveTarget.kind === "note" &&
        (!section.can_edit ||
          exitNeedsReview(moveTarget.note.folder_id, folderId))
      ) {
        setPendingMovePr({ note: moveTarget.note, folderId, sortOrder: null });
        return;
      }
      const id =
        moveTarget.kind === "note" ? moveTarget.note.id : moveTarget.draft.id;
      const next = moveToContainerEnd(layoutRef.current, id, folderId ?? ROOT);
      setLayout(next);
      if (moveTarget.kind === "draft") {
        // 移動先の末尾 = 並び位置の指定なし
        void saveDraftPlacement(moveTarget.draft, folderId, null);
      } else {
        void commitLayout(next);
      }
      // 移動先を開いて結果を見せる
      if (folderId) {
        setOpenFolders((prev) => new Set(prev).add(folderId));
      }
    },
    [
      moveTarget,
      section.can_edit,
      requestFolderMove,
      setLayout,
      commitLayout,
      saveDraftPlacement,
      exitNeedsReview,
    ],
  );

  // ── レンダリング ─────────────────────────────────────────────────

  /** ノート行（カード表示ノートはアイコン + 未投稿バッジで区別する） */
  const renderNoteRow = (note: NoteSummary, depth: number) => (
    <SortableRow
      key={note.id}
      dndId={`note:${note.id}`}
      dragDisabled={dndDisabled}
      dropLine={dropLine?.id === note.id ? dropLine.edge : undefined}
    >
      <NoteItem
        role="button"
        tabIndex={0}
        {...{ [PAGE_ROW_ATTR]: "" }}
        $selected={note.id === selectedNoteId}
        $depth={depth}
        onClick={() => onSelectNote(note.id)}
        onKeyDown={(e) => activateOnKey(e, () => onSelectNote(note.id))}
      >
        {note.display_style === "card" && (
          <ToolTip description="カード（共有TIPS）">
            <span className="card-icon material-symbols-rounded">style</span>
          </ToolTip>
        )}
        {note.display_style === "board" && (
          <ToolTip description="イメージボード">
            <span className="card-icon material-symbols-rounded">imagesmode</span>
          </ToolTip>
        )}
        <span className="note-title bodytext-13 u-line-height-150">
          {note.title}
        </span>
        {note.display_style === "card" && !note.is_published && (
          <StatusBadge label="未投稿" size="small" />
        )}
        {note.has_my_draft && (
          <ToolTip description="自分の未提出の変更（下書き）があります。編集で引き継がれます">
            <StatusBadge label="下書きあり" size="small" />
          </ToolTip>
        )}
        <RowActions label={`${note.title} の操作メニュー`}>
          <DropdownMenu items={buildNoteMenuItems(note)} size="small" />
        </RowActions>
      </NoteItem>
    </SortableRow>
  );

  /**
   * 自分の下書き（未提出の新規作成提案）の行。配置（フォルダ・並び位置）に
   * 従ってツリーの中に出し、ドラッグや「移動...」で配置を変えられる。
   * クリックで通常ノートと同じページ表示（?draft=）で開き、編集・提出・
   * 取り下げができる
   */
  const renderDraftRow = (draft: NoteDraftSummary, depth: number) => (
    <SortableRow
      key={draft.id}
      dndId={`draft:${draft.id}`}
      dragDisabled={draftDragDisabled}
      dropLine={dropLine?.id === draft.id ? dropLine.edge : undefined}
    >
      <NoteItem
        role="button"
        tabIndex={0}
        {...{ [PAGE_ROW_ATTR]: "" }}
        $selected={draft.id === selectedDraftId}
        $depth={depth}
        onClick={() => onSelectDraft(draft.id)}
        onKeyDown={(e) => activateOnKey(e, () => onSelectDraft(draft.id))}
      >
        {draft.display_style === "card" && (
          <ToolTip description="カード（共有TIPS）">
            <span className="card-icon material-symbols-rounded">style</span>
          </ToolTip>
        )}
        <span className="note-title bodytext-13 u-line-height-150">
          {draft.title}
        </span>
        <StatusBadge label="下書き" size="small" />
        <RowActions label={`${draft.title} の操作メニュー`}>
          <DropdownMenu items={buildDraftMenuItems(draft)} size="small" />
        </RowActions>
      </NoteItem>
    </SortableRow>
  );

  /** フォルダ行 + 開いた中身（混在リスト）を再帰描画する */
  const renderFolderRow = (
    folder: NoteFolderSummary,
    depth: number,
  ): React.ReactNode => (
    <FolderTreeRows
      key={folder.id}
      folder={folder}
      depth={depth}
      isOpen={openFolders.has(folder.id)}
      renaming={renamingFolderId === folder.id}
      renameValue={renameValue}
      renameInputRef={renameInputRef}
      menuItems={buildFolderMenuItems(folder)}
      dragDisabled={dndDisabled}
      dropLine={dropLine?.id === folder.id ? dropLine.edge : undefined}
      onNativeFileDrop={
        canDropFiles
          ? (entries) => void handleNativeFileDrop(folder, entries)
          : undefined
      }
      onToggle={() => toggleFolder(folder.id)}
      onChangeRename={setRenameValue}
      onCommitRename={() => void commitRename()}
      onCancelRename={() => setRenamingFolderId(null)}
    >
      {renderContainer(folder.id, depth + 1)}
    </FolderTreeRows>
  );

  /**
   * コンテナの行（フォルダ / ノート / 下書き混在）を 1 つの SortableContext で
   * 束ねて描画する。並び順は種別を問わず layout の順そのまま。
   * ドラッグ可否は行ごとに決める（公開済みの行はメンテナー以上、下書きは本人）
   */
  const renderContainer = (
    containerId: string,
    depth: number,
  ): React.ReactNode => {
    const list = layout.items[containerId] ?? [];
    return (
      <SortableContext
        items={list.map(itemDndId)}
        strategy={noShiftStrategy}
      >
        {list.map((it) => {
          if (it.kind === "folder") return renderFolderRow(it.folder, depth);
          if (it.kind === "note") return renderNoteRow(it.note, depth);
          return renderDraftRow(it.draft, depth);
        })}
      </SortableContext>
    );
  };

  const showHeaderlessActions =
    hideSectionHeader && sectionMenuItems.length > 0;
  // 見出し行が無いと開く手段が無いため、見出し非表示では常に開いて見せる
  const bodyHidden = collapsed && !hideSectionHeader;

  return (
    <SectionBlock
      $fileDropActive={nativeOverSection}
      {...sectionNativeDropHandlers}
    >
      {!hideSectionHeader && (
        <SectionHeaderRow $bannerColor={section.banner_color ?? undefined}>
          <div className="section-header-inner">
            <SectionToggle
              type="button"
              $open={!collapsed}
              onClick={onToggleCollapsed}
              aria-expanded={!collapsed}
              aria-label={`${section.title} を${collapsed ? "展開する" : "折りたたむ"}`}
            >
              <span className="material-symbols-rounded">chevron_right</span>
            </SectionToggle>
            <h3 className="section-title heading-13 u-line-height-200">
              {section.title}
            </h3>
            {/* 一括インポート（直接更新不可のセクションでは下書きとして取り込む） */}
            {section.can_contribute && (
              <IconButton
                onClick={() => onImportNotes(section)}
                title="Markdownファイルをインポート"
                aria-label={`${section.title} にインポート`}
              >
                <span className="material-symbols-rounded">upload_file</span>
              </IconButton>
            )}
            {/* Markdown 書き出し（閲覧できるノートは誰でもダウンロード可） */}
            {section.notes.length > 0 && (
              <IconButton
                onClick={() => onExportSection(section)}
                title="全ノート/カードを Markdown で書き出し（.zip）"
                aria-label={`${section.title} のノート/カードを書き出し`}
              >
                <span className="material-symbols-rounded">download</span>
              </IconButton>
            )}
            {(canManage || section.can_manage_roles) && (
              <IconButton
                onClick={() => onOpenSectionSettings(section)}
                title="セクション設定"
                aria-label={`${section.title} の設定`}
              >
                <span className="material-symbols-rounded">settings</span>
              </IconButton>
            )}
            {/* 作成系（ノート/カード/フォルダ）の3点リーダーメニュー */}
            {sectionMenuItems.length > 0 && (
              <DropdownMenu items={sectionMenuItems} size="small" />
            )}
          </div>
        </SectionHeaderRow>
      )}

      {/* 見出し非表示（プロジェクト内埋め込み）でも作成メニューは使えるようにする */}
      {showHeaderlessActions && (
        <SectionActionsRow>
          <DropdownMenu items={sectionMenuItems} size="small" />
        </SectionActionsRow>
      )}

      {!bodyHidden && section.can_contribute && (
        <ChangesRow
          $selected={selectedChangesSectionId === section.id}
          onClick={() => onOpenChanges(section)}
        >
          <span className="material-symbols-rounded">merge</span>
          <span className="changes-label bodytext-13 u-line-height-150">
            プルリクエスト
          </span>
          {section.open_change_count > 0 && (
            <span className="changes-count bodytext-11">
              {section.open_change_count}
            </span>
          )}
        </ChangesRow>
      )}

      {!bodyHidden && (
        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetection}
          // ドラッグ中に行がコンテナ間を移動して高さが変わるため、
          // ドロップ先の矩形を常に再計測してズレを防ぐ
          measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          {/* セクション直下の行（フォルダ / ノート / 下書き混在） */}
          {renderContainer(ROOT, 0)}
          {/* フォルダがあるときだけ、ドラッグ中に「セクション直下へ移動」の
              受け皿をツリー末尾に出す（末尾のフォルダ行と取り違えないよう、
              行とは別の独立したドロップ先にする） */}
          {activeDrag != null &&
            (section.can_edit || activeDrag.kind === "draft") &&
            folderTree.length > 0 && <RootMoveDropZone />}

          <DragOverlay dropAnimation={null}>
            {activeDrag && (
              <DragGhostRow>
                <span className="material-symbols-rounded">
                  {activeDrag.icon}
                </span>
                <span>{activeDrag.label}</span>
              </DragGhostRow>
            )}
          </DragOverlay>
        </DndContext>
      )}

      {renamingNote && (
        <NoteRenameModal
          initialTitle={renamingNote.title}
          saving={renamingNoteSaving}
          onSave={(title) => void commitNoteRename(title)}
          onClose={() => setRenamingNote(null)}
        />
      )}

      {moveTarget && (
        <NoteMoveModal
          targetLabel={
            moveTarget.kind === "note"
              ? moveTarget.note.title
              : moveTarget.kind === "draft"
                ? moveTarget.draft.title
                : moveTarget.folder.title
          }
          folders={section.folders}
          currentFolderId={
            moveTarget.kind === "note"
              ? containerFolderId(layout, moveTarget.note.id)
              : moveTarget.kind === "draft"
                ? containerFolderId(layout, moveTarget.draft.id)
                : (moveTarget.folder.parent_id ?? null)
          }
          excludedFolderIds={
            moveTarget.kind === "folder"
              ? descendantIdsOf(moveTarget.folder.id)
              : undefined
          }
          onMove={handleMoveTo}
          onClose={() => setMoveTarget(null)}
        />
      )}

      {shareFolder && (
        <FileBrowserShareModal
          target={{ type: "folder", id: shareFolder.id, name: shareFolder.title }}
          adapter={shareAdapter}
          onClose={() => setShareFolder(null)}
        />
      )}

      {pendingMovePr && (
        <ConfirmModal
          message={
            folderSkips(pendingMovePr.note.folder_id)
              ? `「${pendingMovePr.note.title}」は承認不要フォルダにあるため、外へ移動するにはプルリクエストの承認が必要です。\n「${folderLabel(pendingMovePr.folderId)}」への移動のプルリクエストを送信しますか？`
              : `「${pendingMovePr.note.title}」を「${folderLabel(pendingMovePr.folderId)}」へ移動するプルリクエストを送信しますか？\n承認されると移動します。`
          }
          confirmLabel="送信"
          loading={sendingMovePr}
          onConfirm={() => void sendMovePr()}
          onCancel={() => setPendingMovePr(null)}
        />
      )}

      {pendingPublish && (
        <ConfirmModal
          message={`下書き「${pendingPublish.draft.title}」を承認不要フォルダ「${pendingPublish.folder.title}」に移動します。\nこのまま公開しますか？公開すると他のメンバーにも表示されます。`}
          confirmLabel="公開"
          cancelLabel="下書きのまま"
          onConfirm={() => resolvePendingPublish(true)}
          onCancel={() => resolvePendingPublish(false)}
        />
      )}

      {pendingFolderMove && (
        <ConfirmModal
          message={(() => {
            const { folder, parentId, label } = pendingFolderMove;
            const { notes, cards, boards } = countFolderItems(folder.id);
            const total = notes + cards + boards;
            const head =
              parentId === null
                ? `フォルダ「${folder.title}」をセクション直下へ移動しますか？`
                : `フォルダ「${folder.title}」を「${label}」の中へ移動しますか？`;
            return total > 0
              ? `${head}\n配下のページ ${total}件も一緒に移動します`
              : head;
          })()}
          confirmLabel="移動"
          loading={movingFolder}
          onConfirm={() => void confirmFolderMove()}
          onCancel={() => setPendingFolderMove(null)}
        />
      )}

      {deletingFolder && (
        <ConfirmModal
          message={`フォルダ「${deletingFolder.title}」を削除しますか？\n中のノート/カード・子フォルダは親（またはセクション直下）へ移動します。`}
          confirmLabel="削除"
          onConfirm={() => void handleDeleteFolder()}
          onCancel={() => setDeletingFolder(null)}
        />
      )}

      {purgingFolder &&
        (() => {
          const { notes, cards, boards } = countFolderItems(purgingFolder.id);
          const detail = [
            notes > 0 ? `ノート${notes}件` : null,
            cards > 0 ? `カード${cards}件` : null,
            boards > 0 ? `イメージボード${boards}件` : null,
          ]
            .filter(Boolean)
            .join("・");
          return (
            <ConfirmModal
              message={`フォルダ「${purgingFolder.title}」内の全ファイル（${detail}・子フォルダの中身を含む）を削除しますか？\nフォルダ自体は残ります。この操作は取り消せません。`}
              confirmLabel="削除"
              onConfirm={() => void handlePurgeFolder()}
              onCancel={() => setPurgingFolder(null)}
            />
          );
        })()}
    </SectionBlock>
  );
};

// ── フォルダ行 + 開閉子要素 ──────────────────────────────────────────

interface FolderTreeRowsProps {
  folder: NoteFolderSummary;
  /** 階層の深さ（0 = セクション直下）。インデント幅に使う */
  depth: number;
  isOpen: boolean;
  renaming: boolean;
  renameValue: string;
  renameInputRef: React.RefObject<HTMLInputElement | null>;
  /** 3点リーダーメニューの項目（空のときは3点リーダー自体を出さない） */
  menuItems: DropdownMenuItem[];
  /** D&D（フォルダの階層移動）を無効にするか（can_edit でないとき） */
  dragDisabled: boolean;
  /** 並べ替えでこの行の上 / 下に入るときに線を出す */
  dropLine?: "top" | "bottom";
  /** OS からのファイルドロップでこのフォルダへ取り込む（権限が無ければ未指定） */
  // eslint-disable-next-line no-unused-vars
  onNativeFileDrop?: (entries: FileWithPath[]) => void;
  onToggle: () => void;
  // eslint-disable-next-line no-unused-vars
  onChangeRename: (value: string) => void;
  onCommitRename: () => void;
  onCancelRename: () => void;
  children: React.ReactNode;
}

const FolderTreeRows: React.FC<FolderTreeRowsProps> = ({
  folder,
  depth,
  isOpen,
  renaming,
  renameValue,
  renameInputRef,
  menuItems,
  dragDisabled,
  dropLine,
  onNativeFileDrop,
  onToggle,
  onChangeRename,
  onCommitRename,
  onCancelRename,
  children,
}) => {
  // フォルダ行自体をドロップ先にする（閉じたフォルダへも入れられる）。
  // isOver は collisionDetection が「中に入れる」帯と判定したときだけ立つ
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `folder:${folder.id}`,
  });
  // 乗せている間はグレーが伸び、滞留し終えたら青 = 離せば中に入る
  const dwellReady = useDropDwellReady(isOver);
  // フォルダ行のドラッグ = 同階層の並び替え（sortable）+ 別フォルダ/直下への
  // 階層移動。リネーム中はテキスト選択とぶつかるため無効にする。
  // sortable のノードは行 + 開いた中身のラッパーにし、並び替えアニメーション
  // でフォルダの中身ごと動くようにする（ドラッグ開始は行のリスナーのみ）
  const {
    setNodeRef: setSortableRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `folderdrag:${folder.id}`,
    // ドラッグできないとき（コントリビューター）も、下書きの移動先としての
    // 並べ替え位置（行の上下端）は有効にしておく
    disabled: { draggable: dragDisabled || renaming, droppable: false },
  });

  // OS からのファイルドラッグをこのフォルダで受ける（.md をノート化）。
  // preventDefault 済みのイベントは親（SectionBlock）側で取り込みをスキップ
  // するため、stopPropagation はせずハイライト解除だけ親に任せる。
  const [nativeOver, setNativeOver] = useState(false);
  const handleNativeDragOver = (e: React.DragEvent) => {
    if (!onNativeFileDrop || !containsNativeFiles(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    setNativeOver(true);
  };
  const handleNativeDrop = (e: React.DragEvent) => {
    if (!onNativeFileDrop || !containsNativeFiles(e)) return;
    e.preventDefault();
    setNativeOver(false);
    void extractDropEntries(e).then((entries) => {
      if (entries.length > 0) onNativeFileDrop(entries);
    });
  };

  return (
    <div
      ref={setSortableRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        // ドラッグ中のブロックを前面に出す
        position: "relative",
        zIndex: isDragging ? 10 : undefined,
      }}
    >
      <FolderRow
        ref={setDropRef}
        $dropActive={nativeOver}
        style={
          isOver
            ? dropTargetRowStyle({ isOver, isReady: dwellReady })
            : dropLine
              ? { boxShadow: dropLineShadow(dropLine) }
              : undefined
        }
        $open={isOpen}
        $depth={depth}
        onClick={renaming ? undefined : onToggle}
        aria-expanded={isOpen}
        {...attributes}
        {...listeners}
        onDragOver={handleNativeDragOver}
        onDragLeave={() => setNativeOver(false)}
        onDrop={handleNativeDrop}
      >
        <span className="folder-arrow material-symbols-rounded">
          chevron_right
        </span>
        {renaming ? (
          <FolderRenameInput
            ref={renameInputRef}
            value={renameValue}
            maxLength={255}
            onChange={(e) => onChangeRename(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onBlur={onCommitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") onCommitRename();
              if (e.key === "Escape") onCancelRename();
            }}
          />
        ) : (
          <span className="folder-title bodytext-13 u-line-height-150">
            {folder.title}
          </span>
        )}
        {!renaming && folder.skip_review && (
          <ToolTip description="このフォルダ内（サブフォルダを含む）は、プルリクエストの承認なしでノートを追加・更新できます">
            <StatusBadge label="承認不要" size="small" color="border" />
          </ToolTip>
        )}
        {!renaming && folder.is_shared && (
          <ToolTip description="共有リンクで公開中です（3点リーダーの「共有」から設定・解除）">
            <StatusBadge label="共有中" size="small" color="border" />
          </ToolTip>
        )}
        {!renaming && menuItems.length > 0 && (
          <span
            className="folder-actions"
            // メニュー操作でフォルダ開閉（行クリック）を発火させない
            onClick={(e) => e.stopPropagation()}
            // メニューのタップでフォルダのドラッグを開始させない
            onPointerDown={(e) => e.stopPropagation()}
            aria-label={`${folder.title} の操作メニュー`}
          >
            <DropdownMenu items={menuItems} size="small" />
          </span>
        )}
      </FolderRow>
      {isOpen && children}
    </div>
  );
};

// ── セクション直下のドロップ受け ─────────────────────────────────────

const RootMoveDropZone: React.FC = () => {
  const { setNodeRef, isOver } = useDroppable({ id: "rootzone" });
  const dwellReady = useDropDwellReady(isOver);
  return (
    <RootDropBar ref={setNodeRef} $active={isOver} $ready={dwellReady}>
      <span className="material-symbols-rounded">move_up</span>
      <span className="bodytext-13">セクション直下へ移動</span>
    </RootDropBar>
  );
};

// ── 行の3点リーダー ──────────────────────────────────────────────────

/**
 * 行の右端に置く3点リーダーの受け皿。メニュー操作で行のクリック（選択・
 * 開閉）やドラッグ開始が起きないようにイベントを止める。
 */
const RowActions: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <span
    className="note-actions"
    onClick={(e) => e.stopPropagation()}
    onKeyDown={(e) => e.stopPropagation()}
    onPointerDown={(e) => e.stopPropagation()}
    aria-label={label}
  >
    {children}
  </span>
);

/** ページ行・下書き行の目印（↑↓キーでの移動先を DOM 順に拾う） */
const PAGE_ROW_ATTR = "data-tree-page";

/** ページ行のアイコン（ドラッグ中のゴースト表示用） */
const pageIcon = (style: NoteSummary["display_style"]) => {
  if (style === "card") return "style";
  if (style === "board") return "imagesmode";
  return "description";
};

/**
 * role="button" のページ行のキー操作。Enter / Space で開き、↑ / ↓ で
 * 前後のページ（表示中の行のみ。閉じたフォルダの中は飛ばす。セクションを
 * またぐ）へ移って開く。クリックで選択した直後は行にフォーカスがあるため、
 * そのまま上下キーでページを送れる。
 */
const activateOnKey = (e: React.KeyboardEvent, action: () => void) => {
  if (e.target !== e.currentTarget) return;
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    action();
    return;
  }
  if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
  const row = e.currentTarget as HTMLElement;
  const rows = Array.from(
    row.closest("nav")?.querySelectorAll<HTMLElement>(`[${PAGE_ROW_ATTR}]`) ??
      [],
  );
  const next = rows[rows.indexOf(row) + (e.key === "ArrowDown" ? 1 : -1)];
  if (!next) return;
  e.preventDefault();
  next.focus();
  next.scrollIntoView({ block: "nearest" });
  next.click();
};

// ── サイドバー本体 ───────────────────────────────────────────────────

export interface NotesSidebarProps {
  sections: NoteSection[];
  selectedNoteId: string | null;
  /** 表示中の下書き（変更提案）ID（下書き行のハイライト用） */
  selectedDraftId?: string | null;
  /** PR 一覧を表示中のセクション ID（行のハイライト用） */
  selectedChangesSectionId?: string | null;
  canManage: boolean;
  /**
   * セクション見出し行（バナー）を表示しない。プロジェクト内埋め込みで
   * 表示セクションがそのプロジェクトひとつだけのとき、上のプロジェクト
   * バナーと重複するため非表示にする用途。
   */
  hideSectionHeader?: boolean;
  /**
   * まだ開閉していないセクションを閉じた状態で表示する。グローバルメニューの
   * 「共有ノート」で複数セクションを並べるとき、一覧性を優先する用途。
   */
  defaultSectionsCollapsed?: boolean;
  onSelectNote: (noteId: string) => void;
  /** 自分の下書き（新規作成の変更提案）行を開く */
  // eslint-disable-next-line no-unused-vars
  onSelectDraft: (changeId: string) => void;
  /** セクションのプルリクエスト一覧を開く */
  onOpenChanges: (section: NoteSection) => void;
  /** folder を渡すとそのフォルダ直下に作成する */
  onCreateNote: (section: NoteSection, folder?: NoteFolderSummary) => void;
  /** イメージボードを作成する（folder を渡すとそのフォルダ直下） */
  onCreateBoard: (section: NoteSection, folder?: NoteFolderSummary) => void;
  onImportNotes: (section: NoteSection) => void;
  /** セクション内の全ノート/カードを Markdown（.zip）で書き出す */
  onExportSection: (section: NoteSection) => void;
  /** フォルダ内（子孫を含む）のノート/カードを Markdown（.zip）で書き出す */
  onExportFolder: (section: NoteSection, folder: NoteFolderSummary) => void;
  onCreateSection: () => void;
  onOpenSectionSettings: (section: NoteSection) => void;
  /** リンクコピーをプロジェクトスコープ URL（?tab=notes&note=）にする場合に渡す */
  linkProjectId?: string;
  /** ページ行の「編集」: そのページを開いて編集モードに入る */
  // eslint-disable-next-line no-unused-vars
  onEditNote: (noteId: string) => void;
  /** ページ行の「削除」（確認モーダルは呼び出し側） */
  // eslint-disable-next-line no-unused-vars
  onDeleteNote: (note: NoteSummary) => void;
  /** 下書き行の「下書きを破棄」（確認モーダルは呼び出し側） */
  // eslint-disable-next-line no-unused-vars
  onWithdrawDraft: (draft: NoteDraftSummary) => void;
}

const NotesSidebar: React.FC<NotesSidebarProps> = ({
  sections,
  selectedNoteId,
  selectedDraftId = null,
  selectedChangesSectionId = null,
  canManage,
  hideSectionHeader = false,
  defaultSectionsCollapsed = false,
  onSelectNote,
  onSelectDraft,
  onOpenChanges,
  onCreateNote,
  onCreateBoard,
  onImportNotes,
  onExportSection,
  onExportFolder,
  onCreateSection,
  onOpenSectionSettings,
  linkProjectId,
  onEditNote,
  onDeleteNote,
  onWithdrawDraft,
}) => {
  const {
    isCollapsed: isSectionCollapsed,
    toggle: toggleSection,
    expand: expandSection,
  } = useCollapsedSections("notes-collapsed-sections", defaultSectionsCollapsed);

  // 閉じたセクションの中のページを開いたら（wikilink・PR 一覧からの遷移等）、
  // そのセクションを開いて選択行を見せる。開いた後にユーザーが閉じ直せる
  // よう、同じ選択に対しては一度だけ行う（ツリー再取得では開き直さない）。
  // 表示直後の選択（/notes の先頭ページ自動選択・URL 直指定）では開かない
  // （保存した開閉状態を毎回上書きしないため）
  const autoExpandedForRef = useRef<string | null>(null);
  const initialSelectionSeenRef = useRef(false);
  useEffect(() => {
    const selectionKey = selectedNoteId
      ? `note:${selectedNoteId}`
      : selectedDraftId
        ? `draft:${selectedDraftId}`
        : selectedChangesSectionId
          ? `changes:${selectedChangesSectionId}`
          : null;
    if (!selectionKey || autoExpandedForRef.current === selectionKey) return;
    const target = sections.find((s) =>
      selectedNoteId
        ? s.notes.some((n) => n.id === selectedNoteId)
        : selectedDraftId
          ? s.my_drafts.some((d) => d.id === selectedDraftId)
          : s.id === selectedChangesSectionId,
    );
    // ツリー未取得のうちは判定を保留する
    if (!target) return;
    autoExpandedForRef.current = selectionKey;
    if (!initialSelectionSeenRef.current) {
      initialSelectionSeenRef.current = true;
      return;
    }
    expandSection(target.id);
  }, [
    sections,
    selectedNoteId,
    selectedDraftId,
    selectedChangesSectionId,
    expandSection,
  ]);

  return (
    <SidebarWrapper aria-label="ノート一覧">
      <SectionsList>
        {sections.map((section) => (
          <SectionTreeBlock
            key={section.id}
            section={section}
            selectedNoteId={selectedNoteId}
            selectedDraftId={selectedDraftId}
            selectedChangesSectionId={selectedChangesSectionId}
            canManage={canManage}
            hideSectionHeader={hideSectionHeader}
            collapsed={isSectionCollapsed(section.id)}
            onToggleCollapsed={() => toggleSection(section.id)}
            onSelectNote={onSelectNote}
            onSelectDraft={onSelectDraft}
            onOpenChanges={onOpenChanges}
            onCreateNote={onCreateNote}
            onCreateBoard={onCreateBoard}
            onImportNotes={onImportNotes}
            onExportSection={onExportSection}
            onExportFolder={onExportFolder}
            onOpenSectionSettings={onOpenSectionSettings}
            linkProjectId={linkProjectId}
            onEditNote={onEditNote}
            onDeleteNote={onDeleteNote}
            onWithdrawDraft={onWithdrawDraft}
          />
        ))}

        {canManage && (
          <AddSectionButton onClick={onCreateSection}>
            <span className="material-symbols-rounded">create_new_folder</span>
            <span className="bodytext-13">セクションを追加</span>
          </AddSectionButton>
        )}

        {sections.length === 0 && !canManage && (
          <p
            className="bodytext-13 u-text-light-gray"
            style={{ padding: "0 8px" }}
          >
            閲覧できるノートはありません
          </p>
        )}
      </SectionsList>
    </SidebarWrapper>
  );
};

export default NotesSidebar;
